import { consola } from 'consola'
import type { Ref } from 'vue'
import { isValidUrl, normalizeUrl } from '../../../utils/url/isValidUrl'
import type { Suggestion } from '../useSearchSuggestions/useSearchSuggestions'
import { userEngagementStore } from '../../../store/userData/UserEngagementStore'

const logger = consola.withTag('useSearchboxUrlNavigation')

interface UseSearchboxUrlNavigationOptions {
  enabled: Ref<boolean>
}

interface UseSearchboxUrlNavigation {
  handleSearchInput: (
    input: string,
    event: 'keyboard-return' | 'search-button'
  ) => Promise<void>
  handleSuggestionClick: (suggestion: Suggestion) => Promise<void>
}

/**
 * Composable to handle searchbox URL navigation
 *
 * @param options - Configuration options including the A/B test variant
 * @returns Functions to handle search input and suggestion clicks
 */
export const useSearchboxUrlNavigation = (
  options: UseSearchboxUrlNavigationOptions
): UseSearchboxUrlNavigation => {
  const { track } = useAnalytics()
  const { enabled } = options

  /**
   * Determines the input type and returns appropriate analytics data
   */
  function classifyInput(input: string): {
    inputType: 'url' | 'search'
    normalizedValue: string
  } {
    const isUrl = enabled.value && isValidUrl(input)

    return {
      inputType: isUrl ? 'url' : 'search',
      normalizedValue: isUrl ? normalizeUrl(input) : input,
    }
  }

  /**
   * Tracks legacy query events for control variant
   */
  async function trackLegacyQueryEvent(
    trigger: 'keyboard-return' | 'search-button' | 'suggestion-press',
    isMonetized: boolean | undefined
  ): Promise<void> {
    const engagement = userEngagementStore()
    engagement.engage('search')

    const activeSearchProvider = await useActiveSearchProvider()
    const isMonetizationApiAvailable = isMonetized !== undefined

    if (isMonetizationApiAvailable) {
      if (isMonetized) {
        track('query', {
          location: 'ntp',
          monetized: true,
          trigger,
          active_search_provider: activeSearchProvider,
        })
      } else {
        track('QueryNonMonetized_Submitted', {
          location: 'ntp',
          monetized: false,
          trigger,
          active_search_provider: activeSearchProvider,
        })
      }
    } else {
      track('query', { location: 'ntp', trigger })
    }
  }

  async function handleSearchInput(
    input: string,
    event: 'keyboard-return' | 'search-button'
  ): Promise<void> {
    if (!input || input.trim().length === 0) {
      logger.debug('Empty input, skipping navigation')
      return
    }

    const { inputType, normalizedValue } = classifyInput(input)

    logger.debug('Handling search input:', {
      input,
      inputType,
      normalizedValue,
      enabled: enabled.value,
      trigger: event,
    })

    if (enabled.value) {
      const engagement = userEngagementStore()
      engagement.engage('search')

      track('SearchboxUrlNavigation_InputSubmitted', {
        location: 'ntp',
        variant: 'test',
        input_type: inputType,
        trigger: event,
        input_length: input.length,
        monetized: false,
      })
    }

    try {
      if (inputType === 'url') {
        // Test variant: Navigate to URL
        logger.info('Navigating to URL:', normalizedValue)

        track('SearchboxUrlNavigation_UrlNavigated', {
          location: 'ntp',
          variant: 'test',
          trigger: event,
          input_length: input.length,
        })

        window.open(normalizedValue, '_blank')
      } else {
        // Control variant or search query: Use search
        logger.info('Performing search:', input)

        const { url: searchUrl, isMonetized } =
          await HostBrowserApis.getSearchUrl(input, {
            fallbackUrl: 'https://search.yahoo.com/yhs/search?p={searchTerms}',
            queryPlaceholder: '{searchTerms}',
          })

        if (enabled.value) {
          // Test variant: track new events AND legacy events for BI compatibility
          track('SearchboxUrlNavigation_SearchPerformed', {
            location: 'ntp',
            variant: 'test',
            trigger: event,
            monetized: isMonetized ?? false,
            input_length: input.length,
          })
          await trackLegacyQueryEvent(event, isMonetized)
        } else {
          // Control variant: track legacy query events
          await trackLegacyQueryEvent(event, isMonetized)
        }

        window.open(searchUrl, '_blank')
      }
    } catch (error) {
      logger.error('Error handling search input:', error)

      if (enabled.value) {
        track('SearchboxUrlNavigation_Error', {
          location: 'ntp',
          variant: 'test',
          error_type: 'navigation_failed',
          input_type: inputType,
          error_message:
            error instanceof Error ? error.message : 'Unknown error',
        })
      }

      // Fallback to search on error
      const fallbackSearchUrl = `https://search.yahoo.com/yhs/search?p=${encodeURIComponent(input)}`
      window.open(fallbackSearchUrl, '_blank')
    }
  }

  /**
   * Handles suggestion click from dropdown
   */
  async function handleSuggestionClick(suggestion: Suggestion): Promise<void> {
    const input = suggestion.text

    if (!input) {
      logger.debug('Empty suggestion, skipping navigation')
      return
    }

    logger.debug('Handling suggestion click:', {
      suggestion: input,
      action: suggestion.action,
      enabled: enabled.value,
    })

    // Check if this is a URL navigation action
    if (suggestion.action === 'navigate-url' && enabled.value) {
      const normalizedUrl = normalizeUrl(input)

      logger.info('Navigating to URL from suggestion:', normalizedUrl)

      const engagement = userEngagementStore()
      engagement.engage('search')

      // Track suggestion click
      track('SearchboxUrlNavigation_SuggestionClicked', {
        location: 'ntp',
        variant: 'test',
        trigger: 'suggestion-press',
        input_length: input.length,
        monetized: false,
      })

      // Track URL navigation
      track('SearchboxUrlNavigation_UrlNavigated', {
        location: 'ntp',
        variant: 'test',
        trigger: 'suggestion-press',
        input_length: input.length,
      })

      window.open(normalizedUrl, '_blank')
      return
    }

    // Otherwise, treat as a search query
    try {
      const { url: searchUrl, isMonetized } =
        await HostBrowserApis.getSearchUrl(input, {
          fallbackUrl: 'https://search.yahoo.com/yhs/search?p={searchTerms}',
          queryPlaceholder: '{searchTerms}',
        })

      if (enabled.value) {
        // Test variant: track new events AND legacy events for BI compatibility
        track('SearchboxUrlNavigation_SuggestionClicked', {
          location: 'ntp',
          variant: 'test',
          monetized: isMonetized ?? false,
          input_length: input.length,
          trigger: 'suggestion-press',
        })

        track('SearchboxUrlNavigation_SearchPerformed', {
          location: 'ntp',
          variant: 'test',
          trigger: 'suggestion-press',
          monetized: isMonetized ?? false,
          input_length: input.length,
        })

        await trackLegacyQueryEvent('suggestion-press', isMonetized)
      } else {
        // Control variant: track legacy query events
        await trackLegacyQueryEvent('suggestion-press', isMonetized)
      }

      window.open(searchUrl, '_blank')
    } catch (error) {
      logger.error('Error handling suggestion click:', error)

      if (enabled.value) {
        track('SearchboxUrlNavigation_Error', {
          location: 'ntp',
          variant: 'test',
          error_type: 'suggestion_navigation_failed',
          error_message:
            error instanceof Error ? error.message : 'Unknown error',
        })
      }

      // Fallback to search on error
      const fallbackSearchUrl = `https://search.yahoo.com/yhs/search?p=${encodeURIComponent(input)}`
      window.open(fallbackSearchUrl, '_blank')
    }
  }

  return {
    handleSearchInput,
    handleSuggestionClick,
  }
}
